import { useState } from "react";
import { useTranslation } from "react-i18next";
import { extractErrorMessage } from "@/shared/utils";
import {
  useCreatePurchaseMutation,
  useApprovePurchaseMutation,
  useMarkPaidPurchaseMutation,
  useDeletePurchaseMutation,
  useExtractOCRMutation,
  usePartialUpdatePurchaseMutation,
} from "../api";
import type { CreatePurchaseDto, UpdatePurchaseDto } from "../types";
import type { PromptState } from "./usePurchasesModals";

export interface UsePurchasesMutationsReturn {
  creating: boolean;
  updating: boolean;
  approvingId: number | null;
  markingPaidId: number | null;
  deletingId: number | null;
  extractingOCR: boolean;
  isApproving: boolean;
  isMarkingPaid: boolean;
  isDeleting: boolean;
  handleCreate: (dto: CreatePurchaseDto) => Promise<void>;
  handleUpdate: (id: number, dto: UpdatePurchaseDto) => Promise<void>;
  handleApprove: (id: number) => Promise<void>;
  handleMarkPaid: (id: number, paid_date?: string) => Promise<void>;
  handleDelete: (id: number) => Promise<void>;
  handleExtractOCR: (file: File) => Promise<any>;
  setPrompt: (state: PromptState | null) => void;
}

export function usePurchasesMutations(setPrompt: (state: PromptState | null) => void): UsePurchasesMutationsReturn {
  const { t } = useTranslation("PurchasesPage");

  const [createPurchase] = useCreatePurchaseMutation();
  const [updatePurchase] = usePartialUpdatePurchaseMutation();
  const [approvePurchase] = useApprovePurchaseMutation();
  const [markPaidPurchase] = useMarkPaidPurchaseMutation();
  const [deletePurchase] = useDeletePurchaseMutation();
  const [extractOCR] = useExtractOCRMutation();

  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [markingPaidId, setMarkingPaidId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [extractingOCR, setExtractingOCR] = useState(false);

  const isApproving = approvingId !== null;
  const isMarkingPaid = markingPaidId !== null;
  const isDeleting = deletingId !== null;

  async function handleCreate(dto: CreatePurchaseDto) {
    if (creating) return;
    try {
      setCreating(true);
      await createPurchase(dto).unwrap();
      setPrompt({
        title: t("messages.createSuccessTitle"),
        text: t("messages.createSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.createErrorText", t),
        variant: "error",
      });
    } finally {
      setCreating(false);
    }
  }

  async function handleUpdate(id: number, dto: UpdatePurchaseDto) {
    if (updating) return;
    try {
      setUpdating(true);
      await updatePurchase({ id, body: dto }).unwrap();
      setPrompt({
        title: t("messages.createSuccessTitle"),
        text: t("messages.createSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.createErrorText", t),
        variant: "error",
      });
    } finally {
      setUpdating(false);
    }
  }

  async function handleApprove(id: number) {
    if (isApproving) return;
    try {
      setApprovingId(id);
      await approvePurchase(id).unwrap();
      setPrompt({
        title: t("messages.approveSuccessTitle"),
        text: t("messages.approveSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.approveErrorText", t),
        variant: "error",
      });
    } finally {
      setApprovingId(null);
    }
  }

  async function handleMarkPaid(id: number, paid_date?: string) {
    if (isMarkingPaid) return;
    try {
      setMarkingPaidId(id);
      await markPaidPurchase({ id, paid_date }).unwrap();
      setPrompt({
        title: t("messages.markPaidSuccessTitle"),
        text: t("messages.markPaidSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.markPaidErrorText", t),
        variant: "error",
      });
    } finally {
      setMarkingPaidId(null);
    }
  }

  async function handleDelete(id: number) {
    if (isDeleting) return;
    try {
      setDeletingId(id);
      await deletePurchase(id).unwrap();
      setPrompt({
        title: t("messages.deleteSuccessTitle"),
        text: t("messages.deleteSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.deleteErrorText", t),
        variant: "error",
      });
    } finally {
      setDeletingId(null);
    }
  }

  async function handleExtractOCR(file: File) {
    if (extractingOCR) return;
    try {
      setExtractingOCR(true);
      const result = await extractOCR(file).unwrap();
      return result;
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.ocrErrorText", t),
        variant: "error",
      });
      throw err;
    } finally {
      setExtractingOCR(false);
    }
  }

  return {
    creating,
    updating,
    approvingId,
    markingPaidId,
    deletingId,
    extractingOCR,
    isApproving,
    isMarkingPaid,
    isDeleting,
    handleCreate,
    handleUpdate,
    handleApprove,
    handleMarkPaid,
    handleDelete,
    handleExtractOCR,
    setPrompt,
  };
}

